import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { createFeedback, getBookings } from "../api/api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import AuthModal from "../components/auth/AuthModal";
import { Helmet } from "react-helmet";
import { useLocation } from "wouter";

const feedbackSchema = z.object({
  bookingId: z.string().min(1, { message: "Please select a booking" }),
  rating: z.string().min(1, { message: "Please select a rating" }),
  comment: z.string().optional(),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

const Feedback = () => {
  const { isAuthenticated, user } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [bookings, setBookings] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      bookingId: "",
      rating: "",
      comment: "",
    },
  });

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    getBookings(user.id)
        .then(setBookings)
        .catch(() =>
            toast({
              title: "Error",
              description: "Failed to load bookings.",
              variant: "destructive",
            })
        )
        .finally(() => setLoading(false));
  }, [isAuthenticated, user]);

  const onSubmit = async (data: FeedbackFormValues) => {
    if (!isAuthenticated) {
      setIsAuthModalOpen(true);
      return;
    }

    try {
      await createFeedback(parseInt(data.bookingId), {
        rating: parseInt(data.rating),
        comment: data.comment,
      });
      toast({
        title: "Thank you for your feedback!",
        description: "Your feedback has been submitted successfully.",
      });
      form.reset();
      navigate("/profile");
    } catch (error: any) {
      toast({
        title: "Error submitting feedback",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    }
  };

  return (
      <>
        <Helmet>
          <title>Leave Feedback | SurpriseTable</title>
          <meta
              name="description"
              content="Share your experience with SurpriseTable. Your feedback helps us improve our service."
          />
        </Helmet>

        <section className="bg-gray-50 py-16 transition-colors duration-200 dark:bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
              <div className="mb-8 text-center">
                <h1 className="mb-4 font-serif text-3xl font-bold text-gray-800 dark:text-white">
                  Share Your Experience
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  Your feedback helps us improve our service and assists other diners in making their choices.
                </p>
              </div>

              {!isAuthenticated ? (
                  <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800 text-center">
                    <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-white">
                      Login to Leave Feedback
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                      You need to be logged in to share your dining experience.
                    </p>
                    <Button onClick={() => setIsAuthModalOpen(true)} className="bg-primary font-medium text-white hover:bg-red-700">
                      Login to Continue
                    </Button>
                  </div>
              ) : loading ? (
                  <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="mb-4 h-10 w-full" />
                    ))}
                  </div>
              ) : !bookings || bookings.length === 0 ? (
                  <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800 text-center">
                    <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-white">
                      No Bookings Found
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                      You haven't made any bookings yet. Book a surprise meal first to leave feedback.
                    </p>
                    <Button asChild className="bg-primary font-medium text-white hover:bg-red-700">
                      <a href="/explore">Browse Restaurants</a>
                    </Button>
                  </div>
              ) : (
                  <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800">
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="bookingId"
                            render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Select a Booking</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select a booking" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {bookings.map((b) => (
                                          <SelectItem key={b.id} value={b.id.toString()}>
                                            {b.restaurant?.name || "Restaurant"} on {b.date} at {b.time}
                                          </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Rate Your Experience</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select a rating" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {[5, 4, 3, 2, 1].map((n) => (
                                          <SelectItem key={n} value={n.toString()}>
                                            {"★".repeat(n) + "☆".repeat(5 - n)} ({["Poor", "Fair", "Good", "Very Good", "Excellent"][5 - n]})
                                          </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="comment"
                            render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Your Comments (Optional)</FormLabel>
                                  <FormControl>
                                    <Textarea
                                        placeholder="Tell us about your experience"
                                        rows={4}
                                        {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                            )}
                        />

                        <Button type="submit" className="w-full bg-primary font-medium text-white hover:bg-red-700">
                          Submit Feedback
                        </Button>
                      </form>
                    </Form>
                  </div>
              )}
            </div>
          </div>
        </section>

        <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialView="login" />
      </>
  );
};

export default Feedback;
